import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useFloating } from '@floating-ui/react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import TextField from '@mui/material/TextField';
import type { UserApplication } from '@trinity/chat/api/interfaces/app';
import { Portal } from '@trinity/chat-components/components/Portal/Portal.js';
import { useZIndexMiddleware } from '@trinity/chat-components/components/presentation/popper-modifiers.js';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip.js';
import {
  SetActiveApplicationContext,
  UserApplicationsContext,
  useActiveApplicationIdentifier,
} from '@trinity/chat-components/containers/UserApplications/index.js';
import { ActiveBrandContext } from '@trinity/chat-components/containers/UserBrands/UserBrandsContext.js';
import { Search } from '@/components/icons/Search';
import { useAppSelector } from '@/store';

const SelectorRoot = styled.div({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  marginTop: '5px',
  marginRight: '20px',
  marginLeft: '-1px',
});

const SelectorButton = styled.button(({ theme }) => ({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '8px 12px 8px 0',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  color: theme.isTrinityThemed ? theme.palette.sentiment.primary.main : theme.palette.primary.main,
  '&:hover': {
    color: theme.isTrinityThemed ? theme.palette.sentiment.primary.dark : theme.palette.primary.dark1,
  },
}));

const GridIcon = styled.div(({ theme }) => ({
  width: '24px',
  height: '24px',
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 6px)',
  gridTemplateRows: 'repeat(3, 6px)',
  gap: '2px',
  flexShrink: 0,
  '& > div': {
    backgroundColor: 'currentColor',
    borderRadius: '2px',
    transition: 'background-color 0.3s ease',
  },
  '&:hover > div': {
    '&:nth-of-type(2), &:nth-of-type(4), &:nth-of-type(6), &:nth-of-type(8)': {
      backgroundColor: theme.isTrinityThemed ? theme.palette.sentiment.primary.dark : theme.palette.primary.dark1,
    },
  },
}));

const ActiveAppName = styled.span({
  fontSize: '14px',
  fontWeight: 500,
  maxWidth: '25ch',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const DropdownContainer = styled.div({
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '12px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  minWidth: '300px',
  maxWidth: '400px',
});

const SearchInputWrapper = styled.div({
  marginBottom: '12px',
  '& .MuiTextField-root': {
    width: '100%',
  },
  '& .MuiInputBase-input': {
    padding: '8px 12px',
    fontSize: '14px',
  },
});

const ApplicationsList = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  maxHeight: '300px',
  overflowY: 'auto',
});

const ApplicationListItem = styled.button<{ isActive?: boolean }>(({ theme, isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 16px',
  border: 'none',
  borderRadius: '6px',
  backgroundColor: isActive
    ? (theme.isTrinityThemed 
        ? `${theme.palette.sentiment.primary.main}15` 
        : `${theme.palette.primary.main}15`)
    : 'transparent',
  color: theme.palette.gray.dark1,
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  fontFamily: theme.fonts.regular,
  textAlign: 'left',
  width: '100%',
  '&:hover': {
    backgroundColor: isActive
      ? (theme.isTrinityThemed 
          ? `${theme.palette.sentiment.primary.main}25` 
          : `${theme.palette.primary.main}25`)
      : (theme.isTrinityThemed 
          ? `${theme.palette.sentiment.primary.main}10` 
          : `${theme.palette.primary.main}10`),
  },
}));

const ApplicationIcon = styled.div({
  width: '24px',
  height: '24px',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  overflow: 'hidden',
  flexShrink: 0,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
});

const FallbackIconLetter = styled.div(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.isTrinityThemed 
    ? `${theme.palette.sentiment.primary.main}15` 
    : `${theme.palette.primary.main}15`,
  color: theme.isTrinityThemed 
    ? theme.palette.sentiment.primary.main 
    : theme.palette.primary.main,
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'uppercase',
  borderRadius: '4px',
}));

const ApplicationName = styled.div({
  fontSize: '16px',
  fontWeight: 500,
  color: '#323232',
  lineHeight: '1.4',
  flex: 1,
});

interface ApplicationListItemProps {
  application: UserApplication;
  isActive: boolean;
  onSelect: (app: UserApplication) => void;
}

const ApplicationListItemComponent = memo(({ application, isActive, onSelect }: ApplicationListItemProps) => {
  const handleClick = useCallback(() => {
    onSelect(application);
  }, [application, onSelect]);

  const firstLetter = application.name?.charAt(0).toUpperCase() || '?';

  return (
    <ApplicationListItem onClick={handleClick} isActive={isActive}>
      <ApplicationIcon>
        {application.icon ? (
          <img src={application.icon} alt={application.name} />
        ) : (
          <FallbackIconLetter>{firstLetter}</FallbackIconLetter>
        )}
      </ApplicationIcon>
      <ApplicationName>{application.name}</ApplicationName>
    </ApplicationListItem>
  );
});

export const ApplicationSelector = memo(() => {
  const [open, setOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const activeBrandIdentifier = useContext(ActiveBrandContext);
  const allApplications = useContext(UserApplicationsContext);
  const activeApplicationIdentifier = useActiveApplicationIdentifier();
  const setActiveApplication = useContext(SetActiveApplicationContext);
  
  // Access applicationTooltip feature and isFirstSession from Redux
  const applicationTooltip = useAppSelector((state) => state.features.applicationTooltip);
  const isFirstSession = useAppSelector((state) => state.user.isFirstSession);

  // Filter applications by active brand
  const applications = activeBrandIdentifier ? allApplications[activeBrandIdentifier] || [] : [];

  // Sort applications to show default first
  const sortedApplications = useMemo(() => {
    return [...applications].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return 0;
    });
  }, [applications]);

  // Get active application
  const activeApplication = useMemo(() => {
    return sortedApplications.find(app => app.identifier === activeApplicationIdentifier);
  }, [sortedApplications, activeApplicationIdentifier]);

  // Filter applications by search term (case-insensitive partial matching)
  const filteredApplications = useMemo(() => {
    if (!searchTerm.trim()) {
      return sortedApplications;
    }
    const searchLower = searchTerm.toLowerCase();
    return sortedApplications.filter(app => 
      app.name.toLowerCase().includes(searchLower)
    );
  }, [sortedApplications, searchTerm]);

  // Show tooltip automatically on first session
  useEffect(() => {
    if (isFirstSession && applicationTooltip) {
      setShowTooltip(true);
      // Hide tooltip after 5 seconds
      const timer = setTimeout(() => {
        setShowTooltip(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isFirstSession, applicationTooltip]);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!open) {
      setSearchTerm('');
    }
  }, [open]);

  const toggleVisibility = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleSelection = useCallback(
    (app: UserApplication) => {
      setActiveApplication(app.identifier);
      setOpen(false);
    },
    [setActiveApplication],
  );

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  const zIndexMiddleware = useZIndexMiddleware();
  const { refs, floatingStyles } = useFloating<HTMLButtonElement>({
    placement: 'bottom-start',
    open,
    onOpenChange: setOpen,
    middleware: [zIndexMiddleware],
  });

  const handleBlur = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!refs.domReference.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    },
    [refs.domReference],
  );

  // Don't render if no brand is selected
  if (!activeBrandIdentifier) {
    return null;
  }

  if (applications.length < 2) {
    return null;
  }

  return (
    <SelectorRoot>
      <Tooltip 
        title={applicationTooltip || ''} 
        open={showTooltip}
        onOpen={() => setShowTooltip(true)}
        onClose={() => setShowTooltip(false)}
      >
        <SelectorButton
          onClick={toggleVisibility}
          ref={refs.setReference}
          aria-label="Select Application"
        >
          <GridIcon>
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
          </GridIcon>
          {activeApplication && (
            <ActiveAppName>{activeApplication.name}</ActiveAppName>
          )}
        </SelectorButton>
      </Tooltip>
      {open && (
        <Portal>
          <ClickAwayListener
            mouseEvent="onMouseDown"
            onClickAway={handleBlur}
            touchEvent="onTouchStart"
          >
            <div ref={refs.setFloating} style={floatingStyles}>
              <DropdownContainer>
                <SearchInputWrapper>
                  <TextField
                    placeholder="Search applications in brand..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    variant="outlined"
                    size="small"
                    autoFocus
                    InputProps={{
                      startAdornment: (
                        <Search size={18} />
                      ),
                    }}
                  />
                </SearchInputWrapper>
                <ApplicationsList>
                  {filteredApplications.map((app) => (
                    <ApplicationListItemComponent
                      key={app.identifier}
                      application={app}
                      isActive={app.identifier === activeApplicationIdentifier}
                      onSelect={handleSelection}
                    />
                  ))}
                  {filteredApplications.length === 0 && (
                    <div style={{ 
                      padding: '20px', 
                      textAlign: 'center', 
                      color: '#999',
                      fontSize: '14px'
                    }}>
                      No applications found
                    </div>
                  )}
                </ApplicationsList>
              </DropdownContainer>
            </div>
          </ClickAwayListener>
        </Portal>
      )}
    </SelectorRoot>
  );
});

ApplicationSelector.displayName = 'ApplicationSelector';
